import {useQuery, useQueryClient} from '@tanstack/react-query'
import {useMemo} from 'react'
import {Crown, Trash2, Wifi} from 'lucide-react'
import {usePageTitle} from '@/hooks/use-page-title'
import {Button} from '@/components/ui/button'
import {Card, CardContent} from '@/components/ui/card'
import {PageLayout} from '@/components/shared/PageLayout'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {Badge} from '@/components/ui/badge'
import {StatusBadge, StatusBadgeType} from '@/components/shared/StatusBadge'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {Pagination} from '@/components/ui/pagination'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'
import {ClusterControllerNodesEndpoint} from '@/types/endpoint-contracts'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import {useAuth} from '@/contexts/auth-context'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {InlineRow} from '@/components/shared/InlineRow'
import {Stack} from '@/components/shared/Stack'
import {MonoText} from '@/components/shared/MonoText'
import {MutedText} from '@/components/shared/MutedText'
import {StatValue} from '@/components/shared/StatValue'
import {Icon} from '@/components/shared/Icon'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {ClusterNodeStatus} from '@/types/background-jobs'
import {permissions} from '@/lib/permissions'
import {AppDate, formatDateOnly, formatTimestamp} from '@/lib/date-utils'

export function ClusterNodes() {
    const services = useServices()
    const queryClient = useQueryClient()
    const {hasPermission} = useAuth()
    const i18n = useI18n()
    usePageTitle(i18n.clusterNodes.pageTitle)

    const canAdmin = hasPermission(permissions.jobs.manage)

    const {
        page, pageSize, queryString,
        setPage, setPageSize, pageSizeOptions,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: ClusterControllerNodesEndpoint,
        sortable: true,
        defaultSortBy: ClusterControllerNodesEndpoint.sort.HOSTNAME,
        defaultSortDir: SortDir.ASC,
    })

    const {data: nodesPage, isLoading, error} = useQuery({
        queryKey: ['cluster-nodes', queryString, page, pageSize],
        queryFn: () => services.nodes.list(Object.fromEntries(new URLSearchParams(queryString))),
        refetchInterval: 5000,
    })

    const nodes = nodesPage?.content ?? []
    const totalItems = nodesPage?.totalElements ?? 0
    const totalPages = nodesPage?.totalPages ?? 0

    const {data: currentNode} = useQuery({
        queryKey: ['cluster-current-node'],
        queryFn: () => services.nodes.current(),
    })

    const removeMutation = useEntityMutation({
        mutationFn: (nodeId: string) => services.nodes.remove(nodeId),
        invalidate: ['cluster-nodes'],
        successMessage: i18n.clusterNodes.nodeRemoved,
    })

    const formatTimeSince = (timestamp: string) => {
        const diff = Date.now() - AppDate.epochMillis(timestamp)
        if (diff < 60000) return i18n.common.durationSecondsAgo({count: Math.floor(diff / 1000)})
        if (diff < 3600000) return i18n.common.durationMinutesShortAgo({count: Math.floor(diff / 60000)})
        if (diff < 86400000) return i18n.common.durationHoursShortAgo({count: Math.floor(diff / 3600000)})
        return formatDateOnly(timestamp)
    }

    const hasFilters = useMemo(() => {
        return !!searchAndFilters.q
            || Object.keys(searchAndFilters.filters).length > 0
            || Object.keys(searchAndFilters.dynamicAttributes).length > 0
    }, [searchAndFilters])

    const activeNodes = nodes.filter(n => n.status === ClusterNodeStatus.ACTIVE).length
    const masterNode = nodes.find(n => n.master)

    return (
        <PageLayout
            variant="list"
            icon={CONCEPT_ICON.clusterNode}
            title={i18n.clusterNodes.title as string}
            count={totalItems}
            description={i18n.clusterNodes.subtitle as string}
            loading={isLoading}
            error={error}
            onRetry={() => queryClient.invalidateQueries({queryKey: ['cluster-nodes']})}
            data-testid="cluster-nodes-card"
            filters={
                <UnifiedSearchBar
                    contract={ClusterControllerNodesEndpoint}
                    value={searchAndFilters}
                    onChange={setSearchAndFilters}
                    placeholder={i18n.clusterNodes.searchPlaceholder as string}
                />
            }
            pagination={totalItems > 0 ? (
                <Pagination
                    page={page}
                    pageSize={pageSize}
                    totalItems={totalItems}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                />
            ) : undefined}
            stats={
                <DetailGrid columns={3}>
                    <Card>
                        <CardContent className="pt-6">
                            <InlineRow justify="between">
                                <Stack gap="xs">
                                    <MutedText block>{i18n.clusterNodes.totalNodes}</MutedText>
                                    <StatValue value={totalItems}/>
                                </Stack>
                                <Icon icon={CONCEPT_ICON.clusterNode} size="xl" muted/>
                            </InlineRow>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <InlineRow justify="between">
                                <Stack gap="xs">
                                    <MutedText block>{i18n.clusterNodes.activeNodes}</MutedText>
                                    <StatValue value={activeNodes} variant="success"/>
                                </Stack>
                                <Icon icon={Wifi} size="xl" className="text-status-success"/>
                            </InlineRow>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <InlineRow justify="between">
                                <Stack gap="xs">
                                    <MutedText block>{i18n.clusterNodes.masterNode}</MutedText>
                                    <MonoText className="text-lg truncate" data-testid="master-node-id">
                                        {masterNode?.hostname || i18n.common.none}
                                    </MonoText>
                                </Stack>
                                <Icon icon={Crown} size="xl" className="text-status-warning"/>
                            </InlineRow>
                        </CardContent>
                    </Card>
                </DetailGrid>
            }
        >
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead data-testid="col-node-id">{i18n.clusterNodes.nodeId}</TableHead>
                                <TableHead data-testid="col-hostname">{i18n.clusterNodes.hostname}</TableHead>
                                <TableHead data-testid="col-ip-address">{i18n.clusterNodes.ipAddress}</TableHead>
                                <TableHead data-testid="col-status">{i18n.common.status}</TableHead>
                                <TableHead data-testid="col-last-heartbeat">{i18n.clusterNodes.lastHeartbeat}</TableHead>
                                <TableHead data-testid="col-registered">{i18n.clusterNodes.registered}</TableHead>
                                {canAdmin && <TableHead data-testid="col-actions" className="text-right">{i18n.common.actions}</TableHead>}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {nodes.map((node) => (
                                <TableRow
                                    key={node.nodeId}
                                    data-testid={`row-cluster-node-${node.nodeId}`}
                                    className={node.nodeId === currentNode?.nodeId ? 'bg-accent/50' : ''}
                                >
                                    <TableCell className="font-mono text-sm">
                                        {node.nodeId}
                                        {node.nodeId === currentNode?.nodeId && (
                                            <Badge variant="outline" className="ml-2">{i18n.clusterNodes.thisNode}</Badge>
                                        )}
                                    </TableCell>
                                    <TableCell>{node.hostname}</TableCell>
                                    <TableCell className="font-mono text-sm">{node.ipAddress}</TableCell>
                                    <TableCell>
                                        <InlineRow>
                                            {node.master && <StatusBadge status="MASTER" type={StatusBadgeType.NODE_ROLE}/>}
                                            <StatusBadge status={node.status} type={StatusBadgeType.NODE_STATUS}/>
                                        </InlineRow>
                                    </TableCell>
                                    <TableCell className="text-sm text-muted-foreground">
                                        {formatTimeSince(node.lastHeartbeat)}
                                    </TableCell>
                                    <TableCell className="text-sm text-muted-foreground">
                                        {formatTimestamp(node.registeredAt)}
                                    </TableCell>
                                    {canAdmin && (
                                        <TableCell className="text-right">
                                            {node.status === ClusterNodeStatus.INACTIVE && (
                                                <AlertDialog>
                                                    <AlertDialogTrigger asChild>
                                                        <Button variant="ghost" size="sm" title={i18n.clusterNodes.removeNode as string} data-testid={`btn-remove-node-${node.nodeId}`}>
                                                            <Trash2 className="h-4 w-4"/>
                                                        </Button>
                                                    </AlertDialogTrigger>
                                                    <AlertDialogContent>
                                                        <AlertDialogHeader>
                                                            <AlertDialogTitle>{i18n.clusterNodes.removeNode}</AlertDialogTitle>
                                                            <AlertDialogDescription>
                                                                {i18n.clusterNodes.removeNodeConfirm}
                                                            </AlertDialogDescription>
                                                        </AlertDialogHeader>
                                                        <AlertDialogFooter>
                                                            <AlertDialogCancel>{i18n.common.cancel}</AlertDialogCancel>
                                                            <AlertDialogAction
                                                                onClick={() => removeMutation.mutate(node.nodeId)}
                                                                className="bg-destructive text-destructive-foreground"
                                                            >
                                                                {i18n.clusterNodes.remove}
                                                            </AlertDialogAction>
                                                        </AlertDialogFooter>
                                                    </AlertDialogContent>
                                                </AlertDialog>
                                            )}
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                            {nodes.length === 0 && (
                                <TableEmptyState
                                    colSpan={7}
                                    hasFilters={hasFilters}
                                    matchMessage={i18n.clusterNodes.noNodesMatch}
                                    emptyMessage={i18n.clusterNodes.noNodesFound}
                                />
                            )}
                        </TableBody>
                    </Table>
        </PageLayout>
    )
}
